"use client";

import { useState, type FormEvent } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

type State =
  | { kind: "idle" }
  | { kind: "submitting" }
  | { kind: "ok"; message: string }
  | { kind: "error"; error: string };

const ratingLabels = ["", "Terrible", "Poor", "Mixed", "Good", "Excellent"];

export function ReviewForm({ firmSlug, firmName }: { firmSlug: string; firmName: string }) {
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [state, setState] = useState<State>({ kind: "idle" });

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (rating < 1 || rating > 5) {
      setState({ kind: "error", error: "Pick a rating from 1 to 5 stars." });
      return;
    }
    const form = new FormData(e.currentTarget);
    setState({ kind: "submitting" });

    try {
      const res = await fetch("/api/reviews", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          firmSlug,
          rating,
          email: form.get("email"),
          displayName: form.get("displayName"),
          title: form.get("title"),
          body: form.get("body"),
        }),
      });
      const json = (await res.json()) as {
        ok?: boolean;
        message?: string;
        error?: string;
      };
      if (!res.ok || !json.ok) {
        setState({ kind: "error", error: json.error ?? `Submission failed (${res.status})` });
        return;
      }
      setState({
        kind: "ok",
        message: json.message ?? "Submitted. Check your email.",
      });
    } catch (err) {
      setState({ kind: "error", error: err instanceof Error ? err.message : "Network error" });
    }
  }

  if (state.kind === "ok") {
    return (
      <div className="rounded-[var(--radius-lg)] border border-emerald-500/30 bg-emerald-500/[0.06] p-5">
        <div className="text-[11px] uppercase tracking-[0.14em] text-emerald-300">Review received</div>
        <p className="mt-3 text-sm text-[var(--fg)] leading-relaxed">{state.message}</p>
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      className="rounded-[var(--radius-lg)] border border-[var(--border)] bg-[var(--surface)]/60 p-5"
    >
      <div className="text-[11px] uppercase tracking-[0.14em] text-[var(--fg-subtle)]">
        Write a review of {firmName}
      </div>

      <div className="mt-4">
        <label className="text-xs text-[var(--fg-muted)]">Rating</label>
        <div className="mt-2 flex items-center gap-1">
          {[1, 2, 3, 4, 5].map((n) => {
            const active = (hoverRating || rating) >= n;
            return (
              <button
                type="button"
                key={n}
                onMouseEnter={() => setHoverRating(n)}
                onMouseLeave={() => setHoverRating(0)}
                onClick={() => setRating(n)}
                className={cn(
                  "size-9 rounded-md text-xl transition-colors",
                  active ? "text-amber-300" : "text-[var(--fg-subtle)] hover:text-[var(--fg-muted)]",
                )}
                aria-label={`${n} star${n > 1 ? "s" : ""}`}
              >
                ★
              </button>
            );
          })}
          <span className="ml-3 text-xs text-[var(--fg-muted)]">
            {(hoverRating || rating) > 0 ? ratingLabels[hoverRating || rating] : "Pick a rating"}
          </span>
        </div>
      </div>

      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <Field label="Email (kept private, verification only)" required>
          <input
            name="email"
            type="email"
            required
            autoComplete="email"
            className={inputCls}
            placeholder="you@example.com"
          />
        </Field>
        <Field label="Display name (optional)">
          <input
            name="displayName"
            type="text"
            maxLength={80}
            className={inputCls}
            placeholder="Anonymous trader"
          />
        </Field>
      </div>

      <div className="mt-3">
        <Field label="Title (optional)">
          <input
            name="title"
            type="text"
            maxLength={200}
            className={inputCls}
            placeholder="Worth the price"
          />
        </Field>
      </div>

      <div className="mt-3">
        <Field label="Your experience" required>
          <textarea
            name="body"
            required
            minLength={40}
            maxLength={4000}
            rows={6}
            className={cn(inputCls, "resize-y leading-relaxed")}
            placeholder="What went well, what didn't. Hidden rules that surprised you. Anything that would help another trader decide."
          />
        </Field>
        <div className="mt-1 text-xs text-[var(--fg-subtle)]">
          We email-verify every review before publishing. Your email stays private and is never
          shown publicly.
        </div>
      </div>

      {state.kind === "error" && (
        <div className="mt-4 rounded-[var(--radius)] border border-rose-500/30 bg-rose-500/[0.06] p-3 text-sm text-rose-300">
          {state.error}
        </div>
      )}

      <div className="mt-5 flex items-center gap-3">
        <Button type="submit" disabled={state.kind === "submitting"}>
          {state.kind === "submitting" ? "Submitting…" : "Submit review"}
        </Button>
        <span className="text-xs text-[var(--fg-subtle)]">
          We&apos;ll send a confirmation link to your email.
        </span>
      </div>
    </form>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="text-xs text-[var(--fg-muted)]">
        {label}
        {required && <span className="text-rose-400"> *</span>}
      </span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

const inputCls =
  "w-full h-10 rounded-[var(--radius)] border border-[var(--border)] bg-[var(--bg-elevated)] px-3 text-sm placeholder:text-[var(--fg-subtle)] focus:outline-none focus:border-[var(--brand)]/60 focus:ring-2 focus:ring-[var(--brand)]/20";
